import React, {FC, useEffect, useRef, useState} from 'react';
import {useRecoilValue} from 'recoil';
import {useNavigate} from 'react-router';

import actions from '../../api/user/update';
import {selectedPlan} from '../../state/subscriptions/subscriptions';
import {isValidEmail, isValidFullName} from '../../utils/isValid';
import {RegisterCompanyFormErrorMessagesType} from '../../types/register';
import {EventTargetType} from '../../types/eventTarget';
import useMutateCreateSubscription from '../../hooks/subscriptions/useMutateCreateSubscription';

import RegisterUpgradePageFormComponent from './RegisterUpgradePageFormComponent';
import PlanUpgradeSummaryComponent from './PlanUpgradeSummaryComponent';
import UILoadingIndicatorFixed from '../UICommon/UILoadingIndicatorFixed';

const RegisterUpgradeCompanyForm: FC = () => {
  const navigate = useNavigate();
  const plan = useRecoilValue(selectedPlan);
  const [isCompanyValid, setIsCompanyValid] = useState(false);
  const [triggerScroll, setTriggereScroll] = useState(false);
  const [formValues, setformValues] = useState({
    avatar: '',
    company_name: '',
    invoice_email: '',
    org_no: undefined,
    street: '',
    city: '',
    postal_code: '',
    is_organisation: true,
  });
  const [companyFormErrorMessages, setCompanyFormErrorMessages] =
    useState<RegisterCompanyFormErrorMessagesType>({
      avatar: '',
      company_name: '',
      invoice_email: '',
      org_no: '',
      street: '',
      city: '',
      postal_code: '',
    });
  const summaryRef = useRef<HTMLDivElement>(null);
  const userInfoRef = useRef<HTMLDivElement>(null);
  const companyInfoRef = useRef<HTMLDivElement>(null);
  const {isLoading, isError, error, isSuccess, mutate} =
    useMutateCreateSubscription();

  const onCompanyInputChange = (event: React.ChangeEvent<EventTargetType>) => {
    const {name, value} = event.target;
    setformValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
    setCompanyFormErrorMessages((prevErrorMessages) => ({
      ...prevErrorMessages,
      [name]: '',
    }));
  };

  const setOrganisationAvatar = (file: any) => {
    setformValues((prevValues) => ({
      ...prevValues,
      avatar: file,
    }));
    setCompanyFormErrorMessages((prevErrorMessages) => ({
      ...prevErrorMessages,
      avatar: '',
    }));
  };

  const validateFields = (
      obj: any,
      setFormErrorMessages: Function,
      defaultErrorMessages: any,
      setIsValid: Function,
  ) => {
    let isValid = true;
    const tmpErrorMessages: any = {...defaultErrorMessages};
    let isEmailValid: boolean = true;
    let isFullNameValid: boolean = true;
    let isInvoiceEmailValid: boolean = true;
    let isNumber: boolean = true;
    if (obj.email) {
      isEmailValid = isValidEmail(obj.email);
    }
    if (obj.invoice_email) {
      isInvoiceEmailValid = isValidEmail(obj.invoice_email);
    }
    if (obj.full_name) {
      isFullNameValid = isValidFullName(obj.full_name);
    }
    if (obj.full_name) {
      isFullNameValid = isValidFullName(obj.full_name);
    }
    if (obj.org_no) {
      isNumber = isNaN(obj.org_no) ? false : true;
    }

    for (const key in obj) {
      if (key !== 'reference' && (obj[key] === null || obj[key] === undefined || obj[key] === '')) {
        tmpErrorMessages[key] = `Det här fältet får inte vara tomt.`;
        isValid = false;
      }
    }

    if (!isEmailValid) {
      tmpErrorMessages.email = `Det angivna värdet är inte en giltig e-postadress.`;
      isValid = false;
    }

    if (!isFullNameValid) {
      tmpErrorMessages.full_name = `Fältet ska innehålla namn och efternamn.`;
      isValid = false;
    }

    if (!isInvoiceEmailValid) {
      tmpErrorMessages.invoice_email = `Det angivna värdet är inte en giltig e-postadress.`;
      isValid = false;
    }

    if (!isNumber) {
      tmpErrorMessages.org_no = `Organisationsnummer måste vara ett nummer.`;
      isValid = false;
    }

    setTriggereScroll(!triggerScroll);
    setFormErrorMessages(tmpErrorMessages);
    setIsValid(isValid);
  };

  useEffect(() => {
    if (userInfoRef.current && isCompanyValid) {
      window.scrollTo({
        top: userInfoRef.current.offsetTop - 148,
        behavior: 'smooth',
      });
    }
  }, [isCompanyValid, triggerScroll]);

  const handleSubmit = () => {
    actions
        .createOrganisation(formValues)
        .then((res: any) => {
          mutate({
            type: 'organisation',
            object_id: res.id,
            subscription: plan.selectedId,
          });
        })
        .catch((error) => {
          if (companyInfoRef.current) {
            window.scrollTo({
              top: companyInfoRef.current.offsetTop - 148,
              behavior: 'smooth',
            });
          }
        });
  };

  useEffect(() => {
    if (isSuccess) {
      navigate('/member-portal/');
    }

    if (isError) {
      console.log(error);
    }
  }, [isSuccess, isError, error]);

  const companyFormFields = [
    {
      id: 'org_no_company',
      type: 'text',
      label: 'Organisationsnummer',
      name: 'org_no',
      value: formValues.org_no,
    },
    {
      id: 'company_name_company',
      type: 'text',
      label: 'Organisationsnamn',
      name: 'company_name',
      value: formValues.company_name,
    },
    {
      id: 'invoice_email_company',
      type: 'text',
      label: 'Faktureringsmail',
      name: 'invoice_email',
      value: formValues.invoice_email,
    },
    {
      id: 'reference_company',
      type: 'text',
      label: 'Referens (valfritt)',
      name: 'reference',
    },
    {
      id: 'street_company',
      type: 'text',
      label: 'Faktureringsaddress',
      name: 'street',
      value: formValues.street,
    },
    {
      id: 'city_company',
      type: 'text',
      label: 'Stad',
      name: 'city',
      value: formValues.city,
    },
    {
      id: 'postal_code_company',
      type: 'text',
      label: 'Postnummer',
      name: 'postal_code',
      value: formValues.postal_code,
    },
  ];


  if (isLoading) {
    return <UILoadingIndicatorFixed />;
  }

  return (
    <div className="flex flex-col space-y-12 -mt-2">
      <div ref={companyInfoRef}>
        <RegisterUpgradePageFormComponent
          stepName="Organisationsuppgifter"
          stepOrder={1}
          totalSteps={2}
          formInputsArray={companyFormFields}
          onChange={onCompanyInputChange}
          formErrorMessages={companyFormErrorMessages}
          keyPrefix="cmp-form-"
          setFile={setOrganisationAvatar}
          isDisabled={false}
        />
      </div>
      <div
        className={`flex items-center text-center px-8 py-2 my-4 bg-green-primary 
        text-white text-lg font-semibold w-fit rounded-full cursor-pointer`}
        onClick={() => {
          validateFields(
              formValues,
              setCompanyFormErrorMessages,
              companyFormErrorMessages,
              setIsCompanyValid,
          );
        }}
      >
        Fortsätt
      </div>
      <div ref={summaryRef}>
        <PlanUpgradeSummaryComponent
          stepName="Slutför köp"
          stepOrder={2}
          totalSteps={2}
          selectedPlan={plan.title}
          selectedPlanPrice={plan.price}
          onSubmit={handleSubmit}
          isDisabled={!isCompanyValid}
        />
      </div>
    </div>
  );
};

export default RegisterUpgradeCompanyForm;
